/**
 * Harmonic Grammar Rules Engine
 *
 * Provides chord progression suggestions based on music theory grammar rules.
 * Used by the "Natural Nexts" feature to suggest likely next chords.
 */

import type { Chord, ChordQuality, ChordExtensions, ScaleDegree, Mode, MusicalKey } from '@/types/chord';
import { getNaturalQuality } from '@/utils/chord-helpers';

/**
 * A suggested next chord
 */
export interface ChordSuggestion {
  scaleDegree: ScaleDegree;
  quality: ChordQuality;
  extensions: ChordExtensions;
  confidence: number;  // 0-1, determines ordering and visual prominence
  source: 'rule' | 'pattern' | 'ai';
  reason: string;      // Educational explanation
}

/**
 * Style hints derived from the current progression
 */
export interface StyleHints {
  hasExtensions: boolean;       // User is using add9s, 7ths, etc.
  preferredExtensions: (keyof ChordExtensions)[];
  chromaticTolerance: number;   // 0-1, how often chromatic chords appear
}

/**
 * Context for generating suggestions
 */
export interface GrammarContext {
  lastChord: Chord;
  previousChords: Chord[];
  key: MusicalKey;
  mode: Mode;
  styleHints?: StyleHints;
}

/**
 * Grammar rule: [targetDegree, baseConfidence, reason]
 */
type GrammarRule = [ScaleDegree, number, string];

/**
 * Major key harmonic grammar rules
 * Based on common-practice harmony with contemporary extensions
 */
const MAJOR_GRAMMAR: Record<ScaleDegree, GrammarRule[]> = {
  1: [
    [4, 0.90, 'Classic I-IV progression'],
    [5, 0.85, 'Strong dominant preparation'],
    [6, 0.80, 'Relative minor color'],
    [2, 0.70, 'Supertonic pre-dominant'],
  ],
  2: [
    [5, 0.95, 'ii-V motion (pre-dominant to dominant)'],
    [4, 0.70, 'Subdominant substitution'],
    [7, 0.60, 'Leading tone approach'],
  ],
  3: [
    [6, 0.85, 'Mediant to submediant (stepwise)'],
    [4, 0.80, 'iii-IV common progression'],
    [2, 0.70, 'Circle of fifths motion'],
  ],
  4: [
    [5, 0.95, 'IV-V cadential preparation'],
    [1, 0.80, 'Plagal motion'],
    [2, 0.70, 'IV-ii voice leading'],
  ],
  5: [
    [1, 0.98, 'Authentic resolution (V-I)'],
    [6, 0.85, 'Deceptive cadence (V-vi)'],
    [4, 0.60, 'Retrogression'],
  ],
  6: [
    [4, 0.85, 'vi-IV (pop progression)'],
    [2, 0.80, 'vi-ii circle of fifths'],
    [5, 0.75, 'vi-V for dramatic effect'],
  ],
  7: [
    [1, 0.90, 'Leading tone resolution'],
    [3, 0.70, 'vii-iii (deceptive)'],
  ],
};

/**
 * Minor key harmonic grammar rules
 * Natural minor with common harmonic/melodic minor borrowings
 */
const MINOR_GRAMMAR: Record<ScaleDegree, GrammarRule[]> = {
  1: [
    [4, 0.90, 'i-iv minor progression'],
    [5, 0.85, 'Dominant preparation'],
    [6, 0.80, 'i-VI dramatic lift'],
    [7, 0.70, 'i-VII modal color'],
  ],
  2: [
    [5, 0.95, 'ii°-V motion'],
    [4, 0.70, 'Pre-dominant area'],
  ],
  3: [
    [6, 0.85, 'III-VI relative major area'],
    [4, 0.80, 'Stepwise descent'],
    [7, 0.70, 'III-VII modal'],
  ],
  4: [
    [5, 0.95, 'iv-V half cadence'],
    [1, 0.80, 'Plagal approach'],
    [7, 0.70, 'iv-VII modal'],
  ],
  5: [
    [1, 0.98, 'Authentic resolution (V-i)'],
    [6, 0.85, 'Deceptive to VI'],
    [4, 0.60, 'Retrogression'],
  ],
  6: [
    [7, 0.85, 'VI-VII modal progression'],
    [3, 0.80, 'VI-III major area'],
    [5, 0.75, 'VI-V for intensity'],
    [4, 0.70, 'VI-iv descending'],
  ],
  7: [
    [1, 0.85, 'VII-i resolution'],
    [3, 0.80, 'VII-III common'],
    [6, 0.70, 'VII-VI stepwise'],
  ],
};

/**
 * Get Roman numeral string for display
 */
export function getRomanNumeral(scaleDegree: ScaleDegree, mode: Mode): string {
  const majorNumerals: Record<ScaleDegree, string> = {
    1: 'I', 2: 'ii', 3: 'iii', 4: 'IV', 5: 'V', 6: 'vi', 7: 'vii°',
  };
  const minorNumerals: Record<ScaleDegree, string> = {
    1: 'i', 2: 'ii°', 3: 'III', 4: 'iv', 5: 'V', 6: 'VI', 7: 'VII',
  };
  return mode === 'major' ? majorNumerals[scaleDegree] : minorNumerals[scaleDegree];
}

/**
 * Get the root note name for a scale degree in a given key
 */
export function getChordRootName(key: MusicalKey, scaleDegree: ScaleDegree, mode: Mode): string {
  const majorScaleNotes: Record<MusicalKey, string[]> = {
    'C': ['C', 'D', 'E', 'F', 'G', 'A', 'B'],
    'D': ['D', 'E', 'F#', 'G', 'A', 'B', 'C#'],
    'E': ['E', 'F#', 'G#', 'A', 'B', 'C#', 'D#'],
    'F': ['F', 'G', 'A', 'Bb', 'C', 'D', 'E'],
    'G': ['G', 'A', 'B', 'C', 'D', 'E', 'F#'],
    'A': ['A', 'B', 'C#', 'D', 'E', 'F#', 'G#'],
    'B': ['B', 'C#', 'D#', 'E', 'F#', 'G#', 'A#'],
    'Db': ['Db', 'Eb', 'F', 'Gb', 'Ab', 'Bb', 'C'],
    'Eb': ['Eb', 'F', 'G', 'Ab', 'Bb', 'C', 'D'],
    'Gb': ['Gb', 'Ab', 'Bb', 'Cb', 'Db', 'Eb', 'F'],
    'Ab': ['Ab', 'Bb', 'C', 'Db', 'Eb', 'F', 'G'],
    'Bb': ['Bb', 'C', 'D', 'Eb', 'F', 'G', 'A'],
  };

  // For minor, we use the relative major's notes but starting from the 6th degree
  // For simplicity, just use major scale intervals from the key
  const notes = majorScaleNotes[key] || majorScaleNotes['C'];

  if (mode === 'minor') {
    // In minor, the scale degrees still map to these notes
    // (we're using natural minor, same notes as relative major)
    return notes[scaleDegree - 1];
  }

  return notes[scaleDegree - 1];
}

/**
 * Apply style hints to adjust suggestion extensions
 */
function applyStyleHints(
  suggestions: ChordSuggestion[],
  styleHints?: StyleHints
): ChordSuggestion[] {
  if (!styleHints || !styleHints.hasExtensions) {
    return suggestions;
  }

  // If user uses extensions, suggest them on appropriate chords
  return suggestions.map(suggestion => {
    const newExtensions = { ...suggestion.extensions };

    // Apply preferred extensions based on what user has used
    for (const ext of styleHints.preferredExtensions) {
      // Add extensions that make harmonic sense
      if (ext === 'add9' && ['major', 'minor', 'maj7', 'min7'].includes(suggestion.quality)) {
        newExtensions.add9 = true;
      }
      if (ext === 'sus4' && suggestion.scaleDegree === 5) {
        // Sus4 on dominant is very common
        newExtensions.sus4 = true;
      }
    }

    // Slightly boost confidence for styled suggestions
    const hasNewExtensions = Object.values(newExtensions).some(v => v);
    const confidenceBoost = hasNewExtensions ? 0.02 : 0;

    return {
      ...suggestion,
      extensions: newExtensions,
      confidence: Math.min(1, suggestion.confidence + confidenceBoost),
    };
  });
}

/**
 * Get raw grammar suggestions for a scale degree
 */
function getGrammarRuleSuggestions(
  fromDegree: ScaleDegree,
  mode: Mode
): ChordSuggestion[] {
  const rules = mode === 'major' ? MAJOR_GRAMMAR : MINOR_GRAMMAR;
  const targets = rules[fromDegree] || [];

  return targets.map(([degree, confidence, reason]) => ({
    scaleDegree: degree,
    quality: getNaturalQuality(degree, mode),
    extensions: {},
    confidence,
    source: 'rule' as const,
    reason,
  }));
}

/**
 * Detect style patterns from a progression
 */
export function detectProgressionStyle(chords: Chord[]): StyleHints {
  if (chords.length === 0) {
    return {
      hasExtensions: false,
      preferredExtensions: [],
      chromaticTolerance: 0,
    };
  }

  // Check for extensions
  const extensionCounts: Record<string, number> = {};
  let totalExtensions = 0;

  for (const chord of chords) {
    for (const [key, value] of Object.entries(chord.extensions)) {
      if (value === true) {
        extensionCounts[key] = (extensionCounts[key] || 0) + 1;
        totalExtensions++;
      }
    }
  }

  const hasExtensions = totalExtensions > 0;

  // Sort extensions by frequency
  const preferredExtensions = Object.entries(extensionCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([key]) => key as keyof ChordExtensions);

  // Calculate chromatic tolerance
  const chromaticCount = chords.filter(c => c.isChromatic).length;
  const chromaticTolerance = chromaticCount / Math.max(chords.length, 1);

  return {
    hasExtensions,
    preferredExtensions,
    chromaticTolerance,
  };
}

/**
 * Get chord suggestions based on harmonic grammar
 *
 * @param context - The current progression context
 * @returns Up to 3 chord suggestions sorted by confidence
 */
export function getSuggestions(context: GrammarContext): ChordSuggestion[] {
  const { lastChord, previousChords, mode, styleHints } = context;

  // Get base suggestions from grammar rules
  let suggestions = getGrammarRuleSuggestions(lastChord.scaleDegree, mode);

  // Apply style hints (extensions matching)
  const detectedStyle = styleHints || detectProgressionStyle(previousChords);
  suggestions = applyStyleHints(suggestions, detectedStyle);

  // Sort by confidence and take top 3
  return suggestions
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 3);
}

/**
 * Format a suggestion as a user-friendly tooltip
 */
export function formatSuggestionTooltip(
  suggestion: ChordSuggestion,
  key: MusicalKey,
  mode: Mode
): string {
  const rootName = getChordRootName(key, suggestion.scaleDegree, mode);
  const roman = getRomanNumeral(suggestion.scaleDegree, mode);
  const qualityName = suggestion.quality === 'major' ? '' :
    suggestion.quality === 'minor' ? 'm' :
    suggestion.quality === 'diminished' ? '°' : suggestion.quality;

  // Add extension notation
  let extStr = '';
  if (suggestion.extensions.add9) extStr += '(add9)';
  if (suggestion.extensions.sus4) extStr += 'sus4';
  if (suggestion.extensions.sus2) extStr += 'sus2';

  return `${rootName}${qualityName}${extStr} (${roman}) - ${suggestion.reason}`;
}
